import { useRef, useEffect, useCallback } from 'react';
import useVideoStore from '../stores/useVideoStore';
import { createDraft, autosaveVideo } from '../api';
import useCsrfToken from '../../ArticleFormNew/hooks/useCsrfToken';

var DEBOUNCE_MS = 1500;

function hasTitleId(store) {
  return store.title_id && store.title_id.trim() !== '';
}

export default function useVideoAutosave() {
  var csrfToken = useCsrfToken();
  var timerRef = useRef(null);
  var isSavingRef = useRef(false);
  var isCreatingRef = useRef(false);

  var ensureDraftExists = useCallback(function() {
    var store = useVideoStore.getState();
    if (store.id) return Promise.resolve(store.id);
    if (isCreatingRef.current) return Promise.resolve(null);

    isCreatingRef.current = true;
    return createDraft(csrfToken)
      .then(function(data) {
        if (data.success && data.video) {
          store.updateFields({
            id: data.video.id,
            slug: data.video.slug,
            autosave_token: data.video.autosave_token
          });
          return data.video.id;
        }
        return null;
      })
      .catch(function() {
        return null;
      })
      .finally(function() {
        isCreatingRef.current = false;
      });
  }, [csrfToken]);

  var save = useCallback(function() {
    var store = useVideoStore.getState();
    if (isSavingRef.current) return;
    if (!store.hasUnsavedChanges()) return;

    var changedFields = store.getChangedFields();
    if (Object.keys(changedFields).length === 0) return;

    if (!store.id && !hasTitleId(store)) return;

    isSavingRef.current = true;
    store.setSaveStatus('saving');

    var doSave = store.id ? Promise.resolve(store.id) : ensureDraftExists();

    doSave.then(function(videoId) {
      if (!videoId) {
        store.setSaveStatus('error');
        isSavingRef.current = false;
        return;
      }

      var currentStore = useVideoStore.getState();
      var fieldsToSave = currentStore.getChangedFields();
      if (Object.keys(fieldsToSave).length === 0) fieldsToSave = changedFields;

      return autosaveVideo(
        csrfToken,
        videoId,
        currentStore.autosave_token,
        fieldsToSave
      ).then(function(result) {
        if (result.status === 409) {
          currentStore.setSaveStatus('error');
          if (window.confirm('A newer version exists. Reload?')) {
            window.location.reload();
          }
        } else if (result.data.success) {
          var video = result.data.video;
          if (video.slug) currentStore.updateField('slug', video.slug);
          if (video.thumbnail_url !== undefined) {
            currentStore.updateField('thumbnail_url', video.thumbnail_url || null);
            currentStore.updateField('thumbnail', null);
            currentStore.updateField('remove_thumbnail', false);
          }
          if (video.has_publish_record !== undefined) currentStore.updateField('has_publish_record', video.has_publish_record);
          if (video.published !== undefined) currentStore.updateField('published', video.published);
          currentStore.markSaved(video.autosave_token);
          if (result.data.redirect_url) {
            window.history.replaceState(null, '', result.data.redirect_url);
          }
        } else {
          currentStore.setSaveStatus('error');
        }
      });
    }).catch(function() {
      useVideoStore.getState().setSaveStatus('error');
    }).finally(function() {
      isSavingRef.current = false;
    });
  }, [csrfToken, ensureDraftExists]);

  var scheduleAutosave = useCallback(function() {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(save, DEBOUNCE_MS);
  }, [save]);

  var saveNow = useCallback(function() {
    if (timerRef.current) clearTimeout(timerRef.current);
    save();
  }, [save]);

  useEffect(function() {
    return function() {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { scheduleAutosave, saveNow };
}
